/**
 * Apollo Loader Link
 * 
 * This Apollo Link middleware intercepts all GraphQL requests and responses
 * to automatically manage the global loader state.
 * 
 * How it works:
 * - ALL GraphQL operations (queries and mutations) trigger the global loader
 * - When a request starts → showLoader() is called
 * - When a request completes (success or error) → hideLoader() is called
 * 
 * The loaderService uses request counting, so:
 * - Loader shows when first request starts (count: 0 → 1)
 * - Loader hides when all requests complete (count: n → 0)
 * 
 * This prevents loader flickering when multiple parallel requests are made.
 */

import { ApolloLink, Observable, FetchResult, Operation } from "@apollo/client";
import { loaderService } from "../services/loaderService";
import { shouldLogPerfToConsole } from "../utils/perfTelemetry";

// Type for the forward function in Apollo Link
type NextLink = (operation: Operation) => Observable<FetchResult>;

// Operations that should NOT trigger the loader (optional customization)
const SILENT_OPERATIONS: string[] = [
  // Add operation names here that shouldn't show loader
  // e.g., 'RefreshToken', 'GetNotifications'
  'RefreshToken','DocumentAssistant','RemoveDocumentHistory','DocumentChatHistory', 'ValidationCoverage'
];

/**
 * Check if an operation should show the global loader.
 * Global loader is opt-in only — pages use section skeletons instead.
 * Pass context: { showLoader: true } for rare blocking operations.
 */
const shouldShowLoader = (operation: Operation): boolean => {
  const context = operation.getContext();
  return context.showLoader === true;
};

/**
 * Apollo Link that manages loader state for all GraphQL operations
 */
export const loaderLink = new ApolloLink((operation: Operation, forward: NextLink): Observable<FetchResult> => {
  const showLoader = shouldShowLoader(operation);
  const operationName = operation.operationName || 'Unknown';
  const startTime = performance.now();

  // Show loader when request starts
  if (showLoader) {
    loaderService.showLoader();
  }

  const operationType = operation.query.definitions[0]?.kind === 'OperationDefinition'
    ? (operation.query.definitions[0] as any).operation
    : 'unknown';

  if (shouldLogPerfToConsole()) {
    console.log(`[GraphQL] ➡️ ${operationType.toUpperCase()} ${operationName} started`);
  }

  return new Observable<FetchResult>((observer: {
    next: (value: FetchResult) => void;
    error: (err: unknown) => void;
    complete: () => void;
  }) => {
    let hasCompleted = false;

    // Helper to hide loader only once per operation
    const hideLoaderOnce = () => {
      if (showLoader && !hasCompleted) {
        hasCompleted = true;
        loaderService.hideLoader();
      }
    };

    const subscription = forward(operation).subscribe({
      next: (result: FetchResult) => {
        const duration = Math.round(performance.now() - startTime);
        const hasErrors = Boolean(result.errors && result.errors.length > 0);

        const nreum = (window as unknown as { NREUM?: { addPageAction?: (name: string, attrs: Record<string, unknown>) => void } }).NREUM;
        if (nreum?.addPageAction) {
          nreum.addPageAction('graphql', {
            operation: operationName,
            type: operationType,
            duration_ms: duration,
            success: !hasErrors,
          });
        }

        if (shouldLogPerfToConsole()) {
          console.log(`[GraphQL] ✅ ${operationType.toUpperCase()} ${operationName} completed in ${duration}ms`);
        }
        observer.next(result);
      },
      error: (error: unknown) => {
        const duration = Math.round(performance.now() - startTime);
        const message = error instanceof Error ? error.message : String(error);

        const nreum = (window as unknown as { NREUM?: { addPageAction?: (name: string, attrs: Record<string, unknown>) => void } }).NREUM;
        if (nreum?.addPageAction) {
          nreum.addPageAction('graphql', {
            operation: operationName,
            type: operationType,
            duration_ms: duration,
            success: false,
            error: message.slice(0, 200),
          });
        }

        if (shouldLogPerfToConsole()) {
          console.log(`[GraphQL] ❌ ${operationType.toUpperCase()} ${operationName} failed in ${duration}ms`, message);
        }
        hideLoaderOnce();
        observer.error(error);
      },
      complete: () => {
        hideLoaderOnce();
        observer.complete();
      },
    });

    // Return cleanup function
    return () => {
      hideLoaderOnce();
      subscription.unsubscribe();
    };
  });
});

export default loaderLink;

